import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantProfessionsApi } from "../../api/tenantProfessionsApi.js";
import { tenantDocumentTypesApi } from "../../api/tenantDocumentTypesApi.js";
import { useIssuerMasterMutations } from "../../hooks/useIssuerMaster.js";
import { toast } from "../Toast.js";
import { Button } from "../ui/Button.js";
import { IssuerValuesForm } from "./IssuerValuesForm.js";
import { ProfessionHolderRoster } from "./ProfessionHolderRoster.js";
import { ProfessionDocumentTypesSection } from "./ProfessionDocumentTypesSection.js";

const inputStyle: React.CSSProperties = {
  height: 34,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "0 10px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
  boxSizing: "border-box",
  flex: 1,
};

/**
 * 資格マスタ（組み込み3資格＋テナントが自由に追加できるカスタム資格）を
 * 1つの一覧として管理する。構造上は完全に同格で、組み込み資格（is_builtin）
 * だけが削除不可（API・UI 両方でブロックされる）。
 */
export function ProfessionMasterPanel() {
  const qc = useQueryClient();
  const [newLabel, setNewLabel] = useState("");
  // Phase 3（document-type-master-phase3.md §2.1）: 資格作成と同時に専用請求書 doc_type も
  // 作成するかどうか。デフォルト ON（資格作成→請求書種別作成の2ステップを1ステップに縮める）。
  const [createInvoiceDocType, setCreateInvoiceDocType] = useState(true);
  // reviewer 指摘（Phase 3レビュー必須5）: 源泉徴収（所得税法204条1項2号）の対象は
  // 司法書士・土地家屋調査士など一部資格に限られ、測量士・行政書士などは対象外。
  // かつ `PUT /tenant-document-types/:docType` は当該 doc_type の書類が1件でもあると
  // withholding_eligible の変更を 422 で拒否するため、誤った既定値のまま書類を作成すると
  // 恒久的に誤った税計算に固定されてしまう。安全側に倒し既定は OFF にする。
  const [withholdingEligible, setWithholdingEligible] = useState(false);
  // 2026-08-14: 資格作成→カードを開く→資格者を登録→担当者ドロップダウンで選ぶ→保存、
  // という複数ステップを1ステップに縮めるため、「資格を追加」フォームに既定の資格者指定を統合する。
  // 資格者は資格種別ごとのフラットな名簿（tenant_profession_holders）に直接氏名を
  // 登録する（docs/design/user-professional-separation.md）。
  const [setHolderNow, setSetHolderNow] = useState(false);
  const [holderName, setHolderName] = useState("");
  const [holderInvoiceNo, setHolderInvoiceNo] = useState("");
  const [holderRegNo, setHolderRegNo] = useState("");

  const professionsQuery = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
  });
  const { saveMut, deleteRuleMut } = useIssuerMasterMutations();

  const createMut = useMutation({
    mutationFn: async (label: string) => {
      const created = await tenantProfessionsApi.create({ qualification_label: label });

      // 既定の資格者の登録。失敗しても資格自体の作成は成功扱いのままにする
      // （資格カードから後でいつでも設定できる）。
      let holderFailed = false;
      let holderError: string | null = null;
      if (setHolderNow && holderName.trim()) {
        try {
          const holder = await tenantProfessionsApi.addHolder(created.profession, {
            name: holderName.trim(),
            professional_registration_number: holderRegNo.trim() || null,
            invoice_registration_number: holderInvoiceNo.trim() || null,
          });
          await tenantProfessionsApi.updateHead(created.profession, {
            qualification_label: created.qualification_label,
            holder_id: holder.id,
          });
        } catch (e) {
          holderFailed = true;
          holderError = (e as Error).message;
        }
      }

      if (!createInvoiceDocType) return { created, docTypeFailed: false, docTypeError: null as string | null, holderFailed, holderError };

      try {
        await tenantDocumentTypesApi.create({
          label: `${created.qualification_label}請求書`,
          profession: created.profession,
          kind: "invoice",
          withholding_eligible: withholdingEligible,
        });
        return { created, docTypeFailed: false, docTypeError: null as string | null, holderFailed, holderError };
      } catch (e) {
        return { created, docTypeFailed: true, docTypeError: (e as Error).message, holderFailed, holderError };
      }
    },
    onSuccess: ({ docTypeFailed, docTypeError, holderFailed, holderError }) => {
      qc.invalidateQueries({ queryKey: ["tenant-professions"] });
      qc.invalidateQueries({ queryKey: ["tenant-document-types"] });
      qc.invalidateQueries({ queryKey: ["tenant-profession-holders"] });
      setNewLabel("");
      setSetHolderNow(false);
      setHolderName("");
      setHolderInvoiceNo("");
      setHolderRegNo("");
      // reviewer 指摘（Phase 3レビュー必須5）: doc_type 作成が失敗した場合に
      // 成功トーストも出てしまう不整合を修正。失敗時は失敗トーストのみ出す。
      const problems: string[] = [];
      if (holderFailed) problems.push(`担当者の設定に失敗しました（${holderError}）`);
      if (docTypeFailed) problems.push(`請求書種別の作成に失敗しました（${docTypeError}）`);
      if (problems.length > 0) {
        toast(`資格は作成されましたが、${problems.join("・")}。資格カードから後で設定できます`, "error");
      } else {
        toast("資格を追加しました");
      }
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (profession: string) => tenantProfessionsApi.remove(profession),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-professions"] });
      toast("資格を削除しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  if (professionsQuery.isLoading) {
    return <div style={{ padding: 28, color: "#64748b" }}>読み込み中...</div>;
  }

  const professions = professionsQuery.data?.data ?? [];

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
        書類に印字する資格者と登録番号を資格ごとに登録します。
        司法書士・行政書士・土地家屋調査士は組み込みの資格として最初から登録されています。
        それ以外の資格（例: 測量士）は下部の「＋ 資格を追加」から登録できます。
        PDF上の表示位置や書式は各テンプレートの編集画面で設定します。
      </div>

      {professions.map((item) => (
        <IssuerValuesForm
          key={item.profession}
          profession={item}
          saving={saveMut.isPending || deleteRuleMut.isPending}
          onSave={(input) => saveMut.mutateAsync(input)}
          onDeleteRule={(rule) => deleteRuleMut.mutate({ profession: item.profession, ruleId: rule.id })}
          onDeleteProfession={item.is_builtin ? undefined : () => deleteMut.mutate(item.profession)}
          deletingProfession={deleteMut.isPending}
          roster={<ProfessionHolderRoster profession={item.profession} />}
        >
          <ProfessionDocumentTypesSection profession={item.profession} qualificationLabel={item.qualification_label} />
        </IssuerValuesForm>
      ))}

      <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 15, color: "#0f172a", fontWeight: 900 }}>資格を追加</h2>
        <p style={{ margin: "0 0 12px", fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
          司法書士・行政書士・土地家屋調査士以外の資格（例: 測量士）を登録できます。
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={inputStyle}
            placeholder="新しい資格名（例: 測量士）"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <Button
            variant="primary"
            disabled={!newLabel.trim() || createMut.isPending}
            onClick={() => createMut.mutate(newLabel.trim())}
          >
            + 資格を追加
          </Button>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: "#475569", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={createInvoiceDocType}
            onChange={(e) => setCreateInvoiceDocType(e.target.checked)}
          />
          この資格専用の請求書も同時に作成する
        </label>
        {createInvoiceDocType && (
          <label style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={withholdingEligible}
              onChange={(e) => setWithholdingEligible(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              この資格の報酬は源泉徴収の対象（司法書士・土地家屋調査士など）
              <br />
              <span style={{ color: "#94a3b8" }}>※ 後から変更できません（請求書を作成後は変更不可）</span>
            </span>
          </label>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: "#475569", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={setHolderNow}
            onChange={(e) => setSetHolderNow(e.target.checked)}
          />
          既定の資格者も一緒に設定する
        </label>
        {setHolderNow && (
          <div style={{ marginTop: 8, padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, display: "grid", gap: 8 }}>
            <input
              style={inputStyle}
              placeholder="氏名（必須）"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="士業登録番号（任意）"
              value={holderRegNo}
              onChange={(e) => setHolderRegNo(e.target.value)}
            />
            <input
              style={inputStyle}
              placeholder="適格請求書発行事業者番号（任意）"
              value={holderInvoiceNo}
              onChange={(e) => setHolderInvoiceNo(e.target.value)}
            />
          </div>
        )}
      </section>
    </div>
  );
}
